import * as React from 'react';
import { Copy, Check } from 'lucide-react';

import { cn } from '../lib/cn';
import { Tooltip } from '../ui/Tooltip';
import { useAppSelector } from '../app/hooks';
import { canSeeObjectIds } from '../lib/roles';
import { selectUser } from '../store/selectors/auth';

const COPIED_FEEDBACK_MS = 1500;

export const DELETED_ACCOUNT_LABEL = 'Deleted account';

// Agreed heading rule: name, else email, else phone. Nothing left means the account is gone.
export const identityHeading = ({ name, email, phone } = {}) => name || email || phone || null;

// Whatever contact detail the heading did not use goes on the second line.
export const identityDetail = ({ name, email, phone } = {}) => {
  const heading = identityHeading({ name, email, phone });
  return [email, phone].filter((value) => value && value !== heading).join(' · ');
};

const APPLE_RELAY = /@privaterelay\.appleid\.com$/i;
export const HIDDEN_APPLE_EMAIL = 'Hidden email (Apple)';
const SIGN_IN_LABELS = {
  google: 'Google sign-in',
  apple: 'Apple sign-in',
  sms: 'Phone sign-in',
  phone: 'Phone sign-in',
};

// Stored E.164 drops the plus; put it back so the number reads as dialable.
const formatPhone = (phone) => (/^\d+$/.test(phone ?? '') ? `+${phone}` : phone || null);

const initialsOf = (text) =>
  String(text || '')
    .split(/\s+/)
    .map((word) => word.match(/[\p{L}\p{N}]/u)?.[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

// A customer on the coach screens: the app name, else a real contact; a relay address stays hidden.
export const customerIdentity = ({
  name,
  email,
  phone,
  is_private_email: isPrivate,
  auth_provider: provider,
} = {}) => {
  const hidden = Boolean(email) && (isPrivate ?? APPLE_RELAY.test(email));
  const reachable = [hidden ? null : email, formatPhone(phone)].filter(Boolean);
  const contacts = hidden ? [...reachable, HIDDEN_APPLE_EMAIL] : reachable;
  const heading = name || contacts[0] || null;
  return {
    heading,
    detail: contacts.filter((value) => value !== heading).join(' · '),
    // Only a real name gives initials; a phone digit or a relay letter would read as one.
    initials: initialsOf(name),
    signIn: SIGN_IN_LABELS[provider] ?? null,
    // Hover text: every detail, the relay address included.
    title: [name, email, formatPhone(phone)].filter(Boolean).join(' · '),
  };
};

// The raw id, visible only to admin+developer, click to copy. Renders nothing for anyone else.
export const ObjectId = ({ id, className }) => {
  const user = useAppSelector(selectUser);
  const [copied, setCopied] = React.useState(false);
  React.useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
    return () => clearTimeout(timer);
  }, [copied]);
  if (!id || !canSeeObjectIds(user)) return null;
  const copy = (event) => {
    event.stopPropagation();
    navigator.clipboard?.writeText(id);
    setCopied(true);
  };
  return (
    <Tooltip content={copied ? 'Copied' : 'Copy id'}>
      <button
        type="button"
        onClick={copy}
        className={cn(
          'inline-flex max-w-full items-center gap-1 rounded font-mono text-[11px] leading-4 opacity-60 transition-opacity hover:opacity-100',
          className
        )}
      >
        <span className="truncate">{id}</span>
        {copied ? (
          <Check className="h-3 w-3 shrink-0" aria-hidden />
        ) : (
          <Copy className="h-3 w-3 shrink-0" aria-hidden />
        )}
      </button>
    </Tooltip>
  );
};

// Heading per the agreed rule, contact detail (or `secondary`) beneath, then the copyable id.
// Inside a clickable row, omit `id` and place an ObjectId beside the row's button.
export const UserIdentity = ({ id, name, email, phone, secondary, className }) => {
  const heading = identityHeading({ name, email, phone });
  const detail = secondary ?? identityDetail({ name, email, phone });
  return (
    <span className={cn('flex min-w-0 flex-col text-left leading-5', className)}>
      {heading ? (
        <span className="truncate font-medium">{heading}</span>
      ) : (
        <span className="truncate italic opacity-70">{DELETED_ACCOUNT_LABEL}</span>
      )}
      {detail ? <span className="truncate text-[11px] leading-4 opacity-70">{detail}</span> : null}
      <ObjectId id={id} className="self-start" />
    </span>
  );
};
